import React, { useState, useEffect, useCallback } from 'react';
import { Package, Eye, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';
import adminApi from '../../lib/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OrdersManager = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const loadOrders = useCallback(async () => {
    try {
      const response = await adminApi.get('/api/admin/orders');
      setOrders(response.data);
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de charger les commandes", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await adminApi.put(`/api/admin/orders/${orderId}/status?status=${newStatus}`, {});
      
      toast({ title: "Statut mis à jour avec succès !" });
      loadOrders();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le statut",
        variant: "destructive"
      });
    }
  };

  const statusColors = {
    pending: 'bg-yellow-500',
    confirmed: 'bg-blue-500',
    shipped: 'bg-purple-500',
    delivered: 'bg-green-500',
    cancelled: 'bg-red-500'
  };

  const statusLabels = {
    pending: 'En attente',
    confirmed: 'Confirmée',
    shipped: 'Expédiée',
    delivered: 'Livrée',
    cancelled: 'Annulée'
  };

  const paymentLabels = {
    unpaid: 'Non payée',
    pending: 'Paiement en cours',
    paid: 'Payée',
    failed: 'Paiement échoué',
    expired: 'Paiement expiré'
  };

  const methodLabels = {
    stripe: 'Carte bancaire (Stripe)',
    paypal: 'PayPal'
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-400 text-lg">Chargement des commandes...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Gestion des Commandes</h1>
          <p className="text-gray-400">{orders.length} commande(s) au total</p>
        </div>
      </div>

      <div className="space-y-4">
        {orders.map((order) => (
          <div
            key={order.id}
            className="bg-gray-900 rounded-lg p-6 border border-gray-800 hover:border-gray-700 transition-colors"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-2">
                  <h3 className="text-white font-semibold text-lg">
                    Commande #{order.id.substring(0, 8).toUpperCase()}
                  </h3>
                  <span className={`px-3 py-1 rounded-full text-white text-sm font-semibold ${statusColors[order.status]}`}>
                    {statusLabels[order.status]}
                  </span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${order.paymentStatus === 'paid' ? 'bg-green-500/15 text-green-400 border border-green-500/25' : 'bg-yellow-500/15 text-yellow-300 border border-yellow-500/25'}`}
                    data-testid={`order-payment-status-${order.id}`}
                  >
                    {paymentLabels[order.paymentStatus] || order.paymentStatus}
                  </span>
                </div>
                {order.paymentMethod && (
                  <p className="text-gray-400 text-sm mb-1" data-testid={`order-payment-info-${order.id}`}>
                    {methodLabels[order.paymentMethod] || order.paymentMethod}
                    {order.transactionId && (
                      <span className="text-gray-500"> · transaction <span className="text-gray-300 font-mono text-xs">{order.transactionId}</span></span>
                    )}
                  </p>
                )}
                <p className="text-gray-400 text-sm">
                  {new Date(order.createdAt).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div className="text-right">
                <p className="text-orange-400 font-bold text-2xl">{order.totalAmount.toLocaleString('fr-FR')} €</p>
                <p className="text-gray-400 text-sm">{order.items.length} article(s)</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 p-4 bg-gray-800 rounded-lg">
              <div>
                <h4 className="text-white font-semibold mb-2">Client</h4>
                <p className="text-gray-400 text-sm">{order.customerName}</p>
                <p className="text-gray-400 text-sm">{order.customerEmail}</p>
                <p className="text-gray-400 text-sm">{order.customerPhone}</p>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-2">Adresse de livraison</h4>
                <p className="text-gray-400 text-sm whitespace-pre-line">{order.customerAddress}</p>
              </div>
            </div>

            <div className="mb-4">
              <h4 className="text-white font-semibold mb-3">Articles commandés</h4>
              <div className="space-y-2">
                {order.items.map((item) => (
                  <div key={`${item.productId}-${item.productName}`} className="flex items-center gap-3 p-3 bg-gray-800 rounded">
                    {item.image && (
                      <img src={item.image} alt={item.productName} className="w-12 h-12 object-cover rounded" />
                    )}
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">{item.productName}</p>
                      <p className="text-gray-400 text-xs">Quantité: {item.quantity}</p>
                      {item.marking === 'custom' ? (
                        <div className="flex items-center gap-2 mt-1">
                          {item.logoUrl && <img src={item.logoUrl} alt="logo" className="w-8 h-8 object-contain bg-white/90 rounded p-0.5" />}
                          <span className="text-purple-300 text-xs">Avec marquage{item.customText ? ` · « ${item.customText} »` : ''}</span>
                        </div>
                      ) : (
                        <p className="text-gray-500 text-xs mt-1">Bracelet vierge</p>
                      )}
                    </div>
                    <p className="text-white font-semibold">{(item.price * item.quantity).toLocaleString('fr-FR')} €</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-300 mb-2">Changer le statut</label>
                <Select value={order.status} onValueChange={(value) => updateOrderStatus(order.id, value)}>
                  <SelectTrigger className="bg-gray-800 text-white border-gray-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="confirmed">Confirmée</SelectItem>
                    <SelectItem value="shipped">Expédiée</SelectItem>
                    <SelectItem value="delivered">Livrée</SelectItem>
                    <SelectItem value="cancelled">Annulée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={() => setSelectedOrder(order)}
                  variant="outline"
                  className="border-gray-700 text-white hover:bg-gray-800"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Détails
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {orders.length === 0 && (
        <div className="text-center py-20 bg-gray-900 rounded-lg border border-gray-800">
          <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg mb-4">Aucune commande pour le moment</p>
          <p className="text-gray-500">Les commandes apparaîtront ici dès qu'un client passe commande</p>
        </div>
      )}
    </div>
  );
};

export default OrdersManager;
